import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';

import type { EmissionFactorSearchParams } from '../types';

export interface EmissionFactorSearchContext {
  scope?: 1 | 2 | 3 | undefined;
  activityId?: string | undefined;
  activityLabel?: string | undefined;
  query?: string | undefined;
  region?: string | undefined;
  year?: number | undefined;
}

interface UseEmissionFactorSearchStateOptions {
  initialParams?: Partial<EmissionFactorSearchParams>;
  context?: EmissionFactorSearchContext;
}

export const BASE_EMISSION_FACTOR_SEARCH_PARAMS: EmissionFactorSearchParams = {
  usedOnly: false,
  page: 1,
  pageSize: 12,
};

function normalizeContextRegion(region: string | undefined): string | undefined {
  const normalized = region?.trim().toUpperCase();
  if (!normalized) return undefined;
  return normalized;
}

function normalizeContextYear(year: number | undefined): number | undefined {
  if (typeof year !== 'number' || !Number.isFinite(year)) return undefined;
  if (year < 1900 || year > 3000) return undefined;
  return Math.trunc(year);
}

export function buildContextSearchParams(
  context?: EmissionFactorSearchContext
): Partial<EmissionFactorSearchParams> {
  if (!context) return {};
  const normalizedQuery = context.query?.trim();
  const normalizedLabel = context.activityLabel?.trim();
  const queryCandidate =
    normalizedQuery && normalizedQuery.length > 0 ? normalizedQuery : normalizedLabel;
  const region = normalizeContextRegion(context.region);
  const year = normalizeContextYear(context.year);
  return {
    ...(context.activityId?.trim() ? { activityId: context.activityId.trim() } : {}),
    ...(queryCandidate ? { query: queryCandidate } : {}),
    ...(region ? { region } : {}),
    ...(year ? { year } : {}),
  };
}

export function getEmissionFactorContextChips(
  context?: EmissionFactorSearchContext
): string[] {
  if (!context) return [];
  const chips: string[] = [];
  const region = normalizeContextRegion(context.region);
  const year = normalizeContextYear(context.year);
  if (context.scope) chips.push(`Scope ${String(context.scope)}`);
  if (context.activityLabel?.trim()) chips.push(context.activityLabel.trim());
  if (context.activityId?.trim()) chips.push(`ID: ${context.activityId.trim()}`);
  if (region) {
    chips.push(`Region ${region}`);
  }
  if (year) {
    chips.push(`Year ${String(year)}`);
  }
  return chips;
}

export function useEmissionFactorSearchState({
  initialParams,
  context,
}: UseEmissionFactorSearchStateOptions) {
  const defaultSearchParams = useMemo<EmissionFactorSearchParams>(
    () => ({
      ...BASE_EMISSION_FACTOR_SEARCH_PARAMS,
      ...initialParams,
      page: 1,
      pageSize: initialParams?.pageSize ?? BASE_EMISSION_FACTOR_SEARCH_PARAMS.pageSize,
    }),
    [initialParams]
  );

  const [searchParams, setSearchParams] = useState<EmissionFactorSearchParams>(
    () => defaultSearchParams
  );
  const [queryInput, setQueryInput] = useState(() => defaultSearchParams.query ?? '');

  const contextParams = useMemo(() => buildContextSearchParams(context), [context]);
  const contextChips = useMemo(() => getEmissionFactorContextChips(context), [context]);
  const hasContext = contextChips.length > 0;

  const isContextApplied = useMemo(() => {
    if (!hasContext) return false;
    const queryMatch =
      contextParams.query === undefined || searchParams.query === contextParams.query;
    const activityIdMatch =
      contextParams.activityId === undefined || searchParams.activityId === contextParams.activityId;
    const regionMatch =
      contextParams.region === undefined || searchParams.region === contextParams.region;
    const yearMatch = contextParams.year === undefined || searchParams.year === contextParams.year;
    return queryMatch && activityIdMatch && regionMatch && yearMatch;
  }, [contextParams, hasContext, searchParams]);

  const handleSearch = () => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        query: queryInput || undefined,
        page: 1,
      })
    );
  };

  const handleSourceChange = (value: string) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        source: value || undefined,
        page: 1,
      })
    );
  };

  const handleRegionChange = (value: string) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        region: value || undefined,
        page: 1,
      })
    );
  };

  const handleMethodChange = (value: string) => {
    const typedValue = value as 'ar4' | 'ar5' | 'ar6' | '';
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        calculationMethod: typedValue || undefined,
        page: 1,
      })
    );
  };

  const handleYearChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        year: event.target.value ? parseInt(event.target.value, 10) : undefined,
        page: 1,
      })
    );
  };

  const handleUnitTypeChange = (value: string) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        unitType: value || undefined,
        page: 1,
      })
    );
  };

  const handleFactorSourceChange = (value: string) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        factorSource: (value || undefined) as 'all' | 'custom' | 'climatiq' | undefined,
        page: 1,
      })
    );
  };

  const handleUsedOnlyToggle = (checked: boolean) => {
    setSearchParams(
      (prev): EmissionFactorSearchParams => ({
        ...prev,
        usedOnly: checked,
        page: 1,
      })
    );
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const hasActiveFilters =
    queryInput !== (defaultSearchParams.query ?? '') ||
    (searchParams.source?.length ?? 0) > 0 ||
    (searchParams.region?.length ?? 0) > 0 ||
    (searchParams.calculationMethod?.length ?? 0) > 0 ||
    (searchParams.unitType?.length ?? 0) > 0 ||
    searchParams.year !== undefined ||
    Boolean(searchParams.factorSource) ||
    Boolean(searchParams.usedOnly) !== Boolean(defaultSearchParams.usedOnly);

  const handleResetFilters = () => {
    setQueryInput(defaultSearchParams.query ?? '');
    setSearchParams({ ...defaultSearchParams });
  };

  const applyContextPreset = () => {
    if (!hasContext) return;
    setSearchParams((prev) => ({
      ...prev,
      ...contextParams,
      page: 1,
    }));
    setQueryInput(contextParams.query ?? defaultSearchParams.query ?? '');
  };

  const clearContextPreset = () => {
    if (!hasContext) return;
    setSearchParams((prev) => ({
      ...prev,
      query: defaultSearchParams.query,
      activityId: defaultSearchParams.activityId,
      region: defaultSearchParams.region,
      year: defaultSearchParams.year,
      page: 1,
    }));
    setQueryInput(defaultSearchParams.query ?? '');
  };

  return {
    defaultSearchParams,
    searchParams,
    queryInput,
    hasActiveFilters,
    contextChips,
    hasContext,
    isContextApplied,
    setQueryInput,
    handleSearch,
    handleSourceChange,
    handleRegionChange,
    handleMethodChange,
    handleYearChange,
    handleUnitTypeChange,
    handleFactorSourceChange,
    handleUsedOnlyToggle,
    handlePageChange,
    handleResetFilters,
    applyContextPreset,
    clearContextPreset,
  };
}
